import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { useTRPC } from '../../lib/trpc.js'
import { createGoogleSurface, loadGoogleMaps, onGoogleRefusal } from './google-maps.js'
import {
  placesKey,
  type LngLat,
  type MapLine,
  type MapPin,
  type MapSurface,
  type ShapeCollection,
} from './surface.js'

function placesOf(frame: LngLat[] | undefined, lines: MapLine[] | undefined, pins: MapPin[]): LngLat[] {
  return frame ?? [...(lines ?? []).flatMap((line) => line.path), ...pins.map((pin) => pin.lngLat)]
}

/** Move the camera only when the places, or the map itself, are new. */
function fitPlaces(
  framed: { current: { surface: MapSurface; key: string } | null },
  surface: MapSurface,
  points: LngLat[],
) {
  const key = placesKey(points)
  if (framed.current?.surface === surface && framed.current.key === key) return
  framed.current = { surface, key }
  if (points.length > 0) surface.fit(points)
}

/**
 * A map that fills its box, drawn by Google when a browser key is configured
 * and by the free OpenFreeMap map otherwise (ADR 020).
 *
 * If Google rejects the key the map is redrawn on the free engine with a line
 * saying why, rather than left as Google's grey error page.
 */
export function MapCanvas({
  pins,
  lines,
  shapes,
  fills,
  onShapeClick,
  onShapeHover,
  frame,
  holdCamera = false,
  className,
  children,
}: {
  pins: MapPin[]
  lines?: MapLine[]
  /** Clickable outlines such as suburbs. Keep the object stable; replacing it redraws them all. */
  shapes?: ShapeCollection | null
  fills?: ReadonlyMap<string, string>
  onShapeClick?: (id: string) => void
  onShapeHover?: (id: string | null) => void
  /** What to fit the view to instead of the pins and lines. */
  frame?: LngLat[]
  /**
   * Leave the camera where the person put it. Fit is the only way to frame
   * the places. A site thumbnail and the service-area picker do not hold:
   * they are the place, so they keep framing it.
   */
  holdCamera?: boolean
  className?: string
  /** Overlays drawn above the map, such as an empty state. */
  children?: ReactNode
}) {
  const trpc = useTRPC()
  const config = useQuery({ ...trpc.routing.mapConfig.queryOptions(), staleTime: Infinity })
  const container = useRef<HTMLDivElement>(null)
  const [surface, setSurface] = useState<MapSurface | null>(null)
  const [refused, setRefused] = useState(false)
  const [broken, setBroken] = useState(false)

  // A failed lookup is the same as no key: the free map needs nothing.
  const settled = config.isSuccess || config.isError
  const key = refused ? null : (config.data?.googleMapsKey ?? null)
  const mapId = config.data?.mapId ?? null
  const engine = key ? 'google' : 'free'

  useEffect(() => {
    const element = container.current
    if (!settled || !element) return

    let cancelled = false
    let made: MapSurface | null = null
    const build = key
      ? loadGoogleMaps(key).then((libraries) => createGoogleSurface(element, libraries, mapId))
      : import('./maplibre-surface.js').then((module) => module.createMapLibreSurface(element))

    build.then(
      (next) => {
        if (cancelled) {
          next.destroy()
          return
        }
        made = next
        setSurface(next)
      },
      () => {
        if (cancelled) return
        if (key) setRefused(true)
        else setBroken(true)
      },
    )

    return () => {
      cancelled = true
      made?.destroy()
      setSurface(null)
    }
  }, [key, mapId, settled])

  useEffect(() => (key ? onGoogleRefusal(() => setRefused(true)) : undefined), [key])

  // A held camera draws new pins and never moves. Fit is the only way to frame
  // them, so a filter or an opened job leaves the zoom and the place alone.
  // Anything else still fits when the places change.
  const framed = useRef<{ surface: MapSurface; key: string } | null>(null)
  const placesRef = useRef<LngLat[]>([])
  placesRef.current = placesOf(frame, lines, pins)

  useEffect(() => {
    if (!surface) return
    const drawn = lines ?? []
    const places = placesOf(frame, drawn, pins)
    placesRef.current = places
    surface.setLines(drawn)
    surface.setPins(pins)
    if (holdCamera || frame) return
    fitPlaces(framed, surface, places)
  }, [frame, holdCamera, lines, pins, surface])

  useEffect(() => {
    if (surface && frame && !holdCamera) fitPlaces(framed, surface, frame)
  }, [frame, holdCamera, surface])

  // The handlers go through refs so a new closure each render does not redraw
  // seventeen hundred suburbs.
  const clickRef = useRef(onShapeClick)
  const hoverRef = useRef(onShapeHover)
  clickRef.current = onShapeClick
  hoverRef.current = onShapeHover

  useEffect(() => {
    if (!surface) return
    surface.setShapes(shapes ?? null, {
      onClick: (id) => clickRef.current?.(id),
      onHover: (id) => hoverRef.current?.(id),
    })
  }, [shapes, surface])

  useEffect(() => {
    if (surface) surface.fillShapes(fills ?? new Map())
  }, [fills, shapes, surface])

  return (
    <div className={cn('relative overflow-hidden', className)}>
      {/* Two boxes on purpose. Both libraries write `position: relative` onto
          the element they draw into — MapLibre from an unlayered stylesheet
          that beats Tailwind, Google inline — so that element cannot be the
          one that fills this box. The outer one is ours and never touched. */}
      <div style={{ position: 'absolute', inset: 0 }}>
        <div key={engine} ref={container} style={{ width: '100%', height: '100%' }} />
      </div>
      {refused ? (
        <p className="absolute bottom-2 left-2 rounded-md bg-white/90 px-2 py-1 text-[11px] text-ink-700 shadow-sm">
          Google Maps refused the browser key, so this is the free map. Check the key allows the
          Maps JavaScript API and this website.
        </p>
      ) : null}
      {broken ? (
        <p className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-xs text-ink-500">
          The map could not be drawn.
        </p>
      ) : null}
      {holdCamera && placesRef.current.length > 0 ? (
        <button
          type="button"
          onClick={() => {
            if (placesRef.current.length > 0) surface?.fit(placesRef.current)
          }}
          title="Fit the map to these jobs"
          disabled={!surface}
          className="absolute top-3 left-3 z-10 rounded-md bg-white/95 px-2.5 py-1 text-xs font-semibold text-ink-800 shadow-sm ring-1 ring-ink-200 hover:bg-white disabled:opacity-50"
        >
          Fit
        </button>
      ) : null}
      {children}
    </div>
  )
}
